import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router";
import { useCurrentPath } from "@/hooks/useCurrentPath";
import { useFullDashboardPath } from "@/hooks/useFullDashboardPath";
import { LinkItem } from "@/components/layouts/DashboardLayout";

type NavMenuProps = {
  item: LinkItem;
};

export function NavMenu({ item }: NavMenuProps) {
  const { isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  const fullDashboardPath = useFullDashboardPath();

  const isActive = (url: string) => {
    if (fullDashboardPath === "") {
      return url === "";
    }
    return fullDashboardPath === url || fullDashboardPath.startsWith(url + "/");
  };

  const isMenuItemActive = (url: string) => {
    return fullDashboardPath === url;
  };

  const handleMenuItemClick = (url: string) => {
    navigate(`/dashboard/${url}`);
    setOpenMobile(false);
  };

  const active = isActive(item.url);

  if (!item.menu || item.menu.length === 0) {
    return null;
  }

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-base outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-6 [&>svg]:shrink-0",
              "h-12"
            )}
          >
            <item.icon className="size-6 mr-1" />
            <span className="leading-12 flex-1">{item.name}</span>
            {item.badge && (
              <SidebarMenuBadge
                className={cn(
                  "bg-white text-black font-semibold border",
                  item.badgeVariant === "outline" &&
                    "bg-brand-neutral-700/50 text-white/80",
                  active && item.badgeVariant === "outline" && "text-white"
                )}
              >
                {item.badge}
              </SidebarMenuBadge>
            )}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-72 rounded-lg"
          side={isMobile ? "bottom" : "right"}
          align="start"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="px-2">
              <h3 className="text-lg">
                You have {item.badge} {`panel${item.badge === "1" ? "" : "s"}`}{" "}
                unlinked
              </h3>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {item.menu.map((menuItem) => {
              return (
                <button
                  key={menuItem.url}
                  className="cursor-pointer w-full"
                  onClick={() => handleMenuItemClick(menuItem.url)}
                >
                  <DropdownMenuItem>
                    <menuItem.icon className="size-4" />
                    Link My {menuItem.name} Account
                  </DropdownMenuItem>
                </button>
              );
            })}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
